import { useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/feature/Layout';
import SEOMeta from '@/components/feature/SEOMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <Layout>
        <div className="pt-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-background-100 rounded-full">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">
              文章未找到
            </h1>
            <p className="text-sm text-foreground-600 mb-6">
              您访问的文章不存在或已被移除
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-lg font-medium text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const seoTitle = `${article.title} - 地址发布页`;
  const seoDesc = article.excerpt;
  const seoKeywords = `${article.categoryName},地址发布页,官方地址入口,${article.title}`;

  return (
    <Layout>
      <SEOMeta
        title={seoTitle}
        description={seoDesc}
        keywords={seoKeywords}
        canonicalUrl={`https://gtucxr.cn/article/${slug}`}
        ogType="article"
      />

      <article className="pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: article.categoryName, to: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          <header className="mb-8">
            <div className="flex items-center gap-2.5 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="bg-primary-100 text-primary-700 text-xs px-3 py-1 rounded-full font-medium hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                {article.categoryName}
              </Link>
            </div>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-5 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                阅读约{article.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {article.views.toLocaleString()} 次阅读
              </span>
            </div>
          </header>

          <div
            className="prose prose-lg max-w-none text-foreground-800
              prose-headings:text-foreground-900 prose-headings:font-heading prose-headings:font-bold
              prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
              prose-ul:my-4 prose-ul:list-disc prose-ul:pl-5
              prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-5
              prose-li:text-foreground-700 prose-li:leading-relaxed prose-li:mb-1
              prose-strong:text-foreground-900 prose-strong:font-semibold
              [&_a]:text-primary-600 [&_a]:no-underline hover:[&_a]:text-primary-700
              prose-img:rounded-xl"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <Link
                to={`/category/${article.category}`}
                className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回{article.categoryName}
              </Link>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      navigator.clipboard?.writeText(window.location.href);
                    }
                  }}
                  className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
                >
                  <i className="ri-share-line"></i>
                  分享文章
                </button>
              </div>
            </div>
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="py-14 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((related) => (
                <Link
                  key={related.slug}
                  to={`/article/${related.slug}`}
                  className="bg-background-50 rounded-xl border border-background-200 p-5 hover:border-primary-200 transition-all duration-300 cursor-pointer block"
                >
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className="bg-primary-100 text-primary-700 text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                      {related.categoryName}
                    </span>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2 line-clamp-2 hover:text-primary-600 transition-colors">
                    {related.title}
                  </h3>
                  <div className="flex items-center gap-3 text-xs text-foreground-400">
                    <span>{related.date}</span>
                    <span>{related.readTime}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            author: {
              '@type': 'Organization',
              name: '地址发布页',
            },
            publisher: {
              '@type': 'Organization',
              name: '地址发布页',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://gtucxr.cn/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.categoryName,
                item: `https://gtucxr.cn/category/${article.category}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />
    </Layout>
  );
}